import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  PencilIcon,
  UsersIcon,
  ChartBarIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { campaignService } from '../../services/campaignService';
import { adminService } from '../../services/adminService';
import { smsService } from '../../services/smsService';
import { SmsCampaignStats } from '../../types/sms';
import { Campaign, CampaignStatus } from '../../types/campaign';
import { formatSmsEligibleStatusLabel } from '../../constants/smsEligibleStatuses';
import { formatCampaignTimezoneLabel } from '../../constants/campaignTimezones';
import { formatScheduleTime12 } from '../../components/Campaign/ScheduleTimeInput';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import Breadcrumb from '../../components/UI/Breadcrumb';
import StatusBadge from '../../components/UI/StatusBadge';
import Modal from '../../components/UI/Modal';
import SectionHeader from '../../components/UI/SectionHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useOptionalWebRTC } from '../../contexts/WebRTCContext';

const CampaignDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const webrtc = useOptionalWebRTC();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showTestCallModal, setShowTestCallModal] = useState(false);
  const [testCallPhone, setTestCallPhone] = useState('');
  const [testCallLoading, setTestCallLoading] = useState(false);
  const [testCallResult, setTestCallResult] = useState<{ success: boolean; message: string; callId?: string } | null>(null);
  const [showTestLeadImportModal, setShowTestLeadImportModal] = useState(false);
  const [testLeadImportCount, setTestLeadImportCount] = useState<10 | 20>(10);
  const [testLeadImportLoading, setTestLeadImportLoading] = useState(false);
  const [testLeadClearLoading, setTestLeadClearLoading] = useState(false);
  const [syncAreaCodesLoading, setSyncAreaCodesLoading] = useState(false);
  const [areaCodeTimezone, setAreaCodeTimezone] = useState<'Eastern' | 'Central' | 'Mountain' | 'Pacific'>('Eastern');
  const [areaCodeCounts, setAreaCodeCounts] = useState<{
    timezone: string;
    totalLeads: number;
    areaCodes: Array<{ areaCode: string; count: number }>;
  } | null>(null);
  const [areaCodeCountsLoading, setAreaCodeCountsLoading] = useState(false);
  const [parkActionLoading, setParkActionLoading] = useState(false);
  const [testLeadImportResult, setTestLeadImportResult] = useState<{ requested: number; imported: number } | null>(null);
  const [testLeadClearResult, setTestLeadClearResult] = useState<{ found: number; deleted: number } | null>(null);
  const [smsStats, setSmsStats] = useState<SmsCampaignStats | null>(null);
  const isUser = user?.role === 'user' || user?.role === 'agent';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setAreaCodeCountsLoading(true);
    campaignService
      .getAreaCodeCounts(id, areaCodeTimezone)
      .then((data) => {
        if (!cancelled) setAreaCodeCounts(data);
      })
      .catch((error) => {
        console.error('Failed to load area code counts', error);
        if (!cancelled) setAreaCodeCounts({ timezone: areaCodeTimezone, totalLeads: 0, areaCodes: [] });
      })
      .finally(() => {
        if (!cancelled) setAreaCodeCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, areaCodeTimezone]);

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
  }, [id]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const [campaignData, statsData] = await Promise.all([
        campaignService.getCampaign(id!),
        campaignService.getCampaignStats(id!)
      ]);
      setCampaign({ ...campaignData, stats: statsData });
      if (campaignData.campaignType === 'text') {
        smsService.getCampaignSmsStats(id!).then(setSmsStats).catch(() => undefined);
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignPark = async (enabled: boolean) => {
    if (!campaign) return;
    const agentCount = campaign.agentIds?.length || 0;
    const ok = window.confirm(
      enabled
        ? `Turn instant connect on for all ${agentCount} agent${agentCount === 1 ? '' : 's'} on this campaign? They park a leg the next time they go Available.`
        : `Turn instant connect off for all ${agentCount} agent${agentCount === 1 ? '' : 's'} on this campaign? Open parked legs hang up immediately.`
    );
    if (!ok) return;
    try {
      setParkActionLoading(true);
      const updated = await adminService.setCampaignParkEnabled(campaign.id, enabled);
      toast.success(
        enabled
          ? `Instant connect enabled for ${updated} agent${updated === 1 ? '' : 's'}`
          : `Instant connect disabled for ${updated} agent${updated === 1 ? '' : 's'}`
      );
    } catch (error: any) {
      toast.error(error?.message || 'Could not change instant connect');
    } finally {
      setParkActionLoading(false);
    }
  };

  const handleAction = async (action: 'start' | 'pause' | 'complete' | 'restart') => {
    if (!campaign) return;

    try {
      setActionLoading(action);
      
      let updatedCampaign;
      switch (action) {
        case 'start':
          updatedCampaign = await campaignService.startCampaign(campaign.id);
          break;
        case 'pause':
          updatedCampaign = await campaignService.pauseCampaign(campaign.id);
          break;
        case 'complete':
          updatedCampaign = await campaignService.completeCampaign(campaign.id);
          break;
        case 'restart':
          updatedCampaign = await campaignService.restartCampaign(campaign.id);
          toast.success('Campaign restarted successfully');
          break;
      }
      
      // Preserve existing stats when updating campaign
      setCampaign({ ...updatedCampaign, stats: campaign.stats });
    } catch (error: any) {
      let errorMessage = `Failed to ${action} campaign`;
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!campaign) return;

    try {
      setDeleteLoading(true);
      await campaignService.deleteCampaign(campaign.id);
      navigate('/campaigns');
    } catch (error: any) {
      let errorMessage = 'Failed to delete campaign';
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleTestCall = async () => {
    if (!campaign || !testCallPhone.trim()) return;

    try {
      setTestCallLoading(true);
      const result = await campaignService.makeTestCall(campaign.id, testCallPhone.trim());
      const callId = result.callId || (result as any)?.data?.callId;
      setTestCallResult({
        success: result.success,
        message: result.message || 'Test call completed',
        callId,
      });
      
      if (result.success) {
        // Clear the phone number on success
        setTestCallPhone('');
      }
    } catch (error: any) {
      let errorMessage = 'Failed to initiate test call';
      
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setTestCallResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setTestCallLoading(false);
    }
  };



  const closeTestCallModal = () => {
    setShowTestCallModal(false);
    setTestCallPhone('');
    setTestCallResult(null);
  };

  const handleImportTestLeads = async () => {
    if (!campaign) return;
    try {
      setTestLeadImportLoading(true);
      const result = await campaignService.importTestLeads(campaign.id, testLeadImportCount);
      setTestLeadImportResult(result);
      toast.success(`Imported ${result.imported} test leads`);
      await fetchCampaign();
    } catch (error: any) {
      let errorMessage = 'Failed to import test leads';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setTestLeadImportLoading(false);
    }
  };

  const closeTestLeadImportModal = () => {
    setShowTestLeadImportModal(false);
    setTestLeadImportCount(10);
    setTestLeadImportResult(null);
    setTestLeadClearResult(null);
  };

  const handleClearTestLeads = async () => {
    if (!campaign) return;
    try {
      setTestLeadClearLoading(true);
      const result = await campaignService.clearTestLeads(campaign.id);
      setTestLeadClearResult(result);
      toast.success(`Cleared ${result.deleted} test leads`);
      await fetchCampaign();
    } catch (error: any) {
      let errorMessage = 'Failed to clear test leads';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setTestLeadClearLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">Campaign not found</h3>
        <p className="mt-1 text-sm text-muted-foreground">The campaign you're looking for doesn't exist.</p>
        <div className="mt-6">
          <Button variant="primary" to="/campaigns">
            Back to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Campaigns', href: '/campaigns' },
    { label: campaign.name, href: `/campaigns/${campaign.id}` },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Header */}
      <Card className="p-6">
        <CardContent className="p-0">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-3">
                <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
                  {campaign.name}
                </h2>
                <StatusBadge status={campaign.status} type="campaign" />
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  campaign.campaignType === 'bot'
                    ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'
                    : campaign.campaignType === 'text'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                }`}>
                  {campaign.campaignType === 'bot'
                    ? 'AI Calling'
                    : campaign.campaignType === 'text'
                      ? 'SMS'
                      : 'Human'}
                </span>
                {campaign.campaignType === 'bot' && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    campaign.botEngine === 'telnyx_assistant'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}>
                    {campaign.botEngine === 'telnyx_assistant'
                      ? 'Telnyx AI'
                      : campaign.botEngine === 'vapi_direct'
                        ? 'VAPI Direct'
                        : campaign.botEngine === 'vapi_bridge'
                          ? 'VAPI Bridge'
                          : 'Custom OpenAI'}
                  </span>
                )}
              </div>
              {campaign.description && (
                <p className="mt-1 text-sm text-muted-foreground">{campaign.description}</p>
              )}
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0 space-x-3">
              <>
                {/* Allow both admin and user roles to access Edit. Restricted users are blocked in Edit page. */}
                <Button variant="outlined" to={`/campaigns/${campaign.id}/edit`}>
                  <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                  Edit
                </Button>
                {isAdmin && (
                  <Button
                    onClick={() => setShowDeleteDialog(true)}
                    variant="destructive"
                  >
                    <TrashIcon className="-ml-1 mr-2 h-4 w-4" />
                    Delete
                  </Button>
                )}
              </>
            </div>
          </div>
        </CardContent>
      </Card>



      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Campaign Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Information */}
          {isAdmin && campaign.campaignType === 'text' && (
            <Card className="p-6">
              <SectionHeader title="SMS Campaign Information" className="mb-4" />
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Campaign Type</label>
                    <p className="mt-1 text-sm text-foreground">Text (SMS)</p>
                  </div>
                  {(campaign as any).textConfig?.eligibleLeadStatuses?.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">SMS eligible lead statuses</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(campaign as any).textConfig.eligibleLeadStatuses.map((status: string) => (
                          <span
                            key={status}
                            className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium"
                          >
                            {formatSmsEligibleStatusLabel(status)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(campaign as any).textConfig?.sendSchedules?.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Daily send windows</label>
                      <ul className="mt-2 space-y-2">
                        {(campaign as any).textConfig.sendSchedules.map(
                          (slot: { startTime: string; endTime: string; timezone: string }, index: number) => (
                            <li
                              key={`${slot.startTime}-${slot.endTime}-${slot.timezone}-${index}`}
                              className="text-sm text-foreground rounded-md border border-border px-3 py-2"
                            >
                              {formatScheduleTime12(slot.startTime)} – {formatScheduleTime12(slot.endTime)}{' '}
                              {formatCampaignTimezoneLabel(slot.timezone)}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                  {(campaign as any).textConfig?.templateBody && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">SMS Template</label>
                      <div className="mt-1 p-3 bg-muted rounded-md">
                        <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">{(campaign as any).textConfig.templateBody}</pre>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {isAdmin && campaign.campaignType !== 'text' && (
            <Card className="p-6">
              <SectionHeader title="Campaign Information" className="mb-4" />
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Call Description</label>
                    <p className="mt-1 text-sm text-foreground">{campaign.callDescription}</p>
                  </div>
                  {campaign.voiceDialSchedules && campaign.voiceDialSchedules.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Daily dial windows</label>
                      <ul className="mt-2 space-y-2">
                        {campaign.voiceDialSchedules.map((slot, index) => (
                          <li
                            key={`${slot.startTime}-${slot.endTime}-${slot.timezone}-${index}`}
                            className="text-sm text-foreground rounded-md border border-border px-3 py-2"
                          >
                            {formatScheduleTime12(slot.startTime)} – {formatScheduleTime12(slot.endTime)}{' '}
                            Eastern Time — {formatCampaignTimezoneLabel(slot.timezone)} leads
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Prompt Script</label>
                    <div className="mt-1 p-3 bg-muted rounded-md">
                      <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">{campaign.promptScript}</pre>
                    </div>
                  </div>
                  {campaign.interestedQuestions && campaign.interestedQuestions.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Questions for Interested Leads</label>
                      <ul className="mt-1 space-y-1">
                        {campaign.interestedQuestions.map((question, index) => (
                          <li key={index} className="text-sm text-foreground">• {question}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conversation Styles - admin only, voice campaigns only */}
          {isAdmin && campaign.campaignType !== 'text' && campaign.conversationStyles && campaign.conversationStyles.length > 0 && (
            <Card className="p-6">
              <SectionHeader title="Conversation Styles" className="mb-4" />
              <CardContent className="p-0">
              <div className="space-y-4">
                {campaign.conversationStyles.map((style, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-muted/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-foreground">{style.name}</h4>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          style.isActive 
                            ? 'bg-success/10 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {style.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Style:</span>
                        <p className="text-foreground capitalize">{style.conversationStyle}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Personality:</span>
                        <p className="text-foreground capitalize">{style.personalityType}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Approach:</span>
                        <p className="text-foreground capitalize">{style.persuasionApproach}</p>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">Voice:</span>
                        <p className="text-foreground capitalize">{style.selectedVoiceId}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </CardContent>
            </Card>
          )}

          {(isAdmin || isUser) && (
            <Card className="p-6">
              <SectionHeader title="Area codes by timezone" className="mb-4" />
              <CardContent className="p-0">
                <p className="text-sm text-muted-foreground mb-4">
                  Lead counts for this campaign, grouped by area code. Use this to decide which numbers to buy.
                </p>
                <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Timezone">
                  {(['Eastern', 'Central', 'Mountain', 'Pacific'] as const).map((tz) => (
                    <button
                      key={tz}
                      type="button"
                      role="tab"
                      aria-selected={areaCodeTimezone === tz}
                      onClick={() => setAreaCodeTimezone(tz)}
                      className={`px-3 py-1.5 text-sm rounded-md border ${
                        areaCodeTimezone === tz
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-foreground hover:bg-muted'
                      }`}
                    >
                      {tz}
                    </button>
                  ))}
                </div>
                {areaCodeCountsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading area codes…</p>
                ) : !areaCodeCounts || areaCodeCounts.areaCodes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No {areaCodeTimezone} leads with an area code yet. Use Sync Area Codes if this looks empty.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mb-3">
                      {areaCodeCounts.totalLeads.toLocaleString()} leads · {areaCodeCounts.areaCodes.length} area codes
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {areaCodeCounts.areaCodes.map((row) => (
                        <div
                          key={row.areaCode}
                          className="min-w-[5.5rem] rounded-md border border-border px-3 py-2"
                        >
                          <div className="text-sm font-semibold text-foreground">{row.areaCode}</div>
                          <div className="text-xs text-muted-foreground">{row.count.toLocaleString()} leads</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Quick Actions & Stats */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card className="p-6">
            <SectionHeader title="Quick Actions" className="mb-4" />
            <CardContent className="p-0">
            <div className="space-y-3">
              {/* Campaign State Actions */}
              {(isAdmin || isUser) && campaign.status === 'draft' && (
                <Button
                  onClick={() => handleAction('start')}
                  variant="primary"
                  loading={actionLoading === 'start'}
                  className="w-full flex items-center justify-center p-3 h-auto"
                >
                  <PlayIcon className="-ml-1 mr-2 h-4 w-4" />
                  <span className="font-medium">Start Campaign</span>
                </Button>
              )}

              {(isAdmin || isUser) && campaign.status === 'active' && (
                <Button
                  onClick={() => handleAction('pause')}
                  variant="warning"
                  loading={actionLoading === 'pause'}
                  className="w-full flex items-center justify-center p-3 h-auto"
                >
                  <PauseIcon className="-ml-1 mr-2 h-4 w-4" />
                  <span className="font-medium">Pause Campaign</span>
                </Button>
              )}

              {(isAdmin || isUser) && campaign.status === 'paused' && (
                <Button
                  onClick={() => handleAction('start')}
                  variant="primary"
                  loading={actionLoading === 'start'}
                  className="w-full flex items-center justify-center p-3 h-auto"
                >
                  <PlayIcon className="-ml-1 mr-2 h-4 w-4" />
                  <span className="font-medium">Resume Campaign</span>
                </Button>
              )}

              {isAdmin && (campaign.status === 'active' || campaign.status === 'paused') && (
                <Button
                  onClick={() => handleAction('complete')}
                  variant="primary"
                  loading={actionLoading === 'complete'}
                  className="w-full flex items-center justify-center p-3 h-auto"
                >
                  <StopIcon className="-ml-1 mr-2 h-4 w-4" />
                  <span className="font-medium">Complete Campaign</span>
                </Button>
              )}

              {isAdmin && campaign.status === 'completed' && (
                <Button
                  onClick={() => handleAction('restart')}
                  variant="primary"
                  loading={actionLoading === 'restart'}
                  className="w-full flex items-center justify-center p-3 h-auto"
                >
                  <PlayIcon className="-ml-1 mr-2 h-4 w-4" />
                  <span className="font-medium">Restart Campaign</span>
                </Button>
              )}

              {isAdmin && campaign.campaignType === 'human' && (
                <>
                  <Button
                    onClick={() => void handleCampaignPark(true)}
                    variant="outlined"
                    loading={parkActionLoading}
                    className="w-full flex items-center justify-center p-3 h-auto"
                  >
                    <span className="font-medium">Enable instant connect</span>
                  </Button>
                  <Button
                    onClick={() => void handleCampaignPark(false)}
                    variant="outlined"
                    loading={parkActionLoading}
                    className="w-full flex items-center justify-center p-3 h-auto"
                  >
                    <span className="font-medium">Disable instant connect</span>
                  </Button>
                </>
              )}
              
              {/* Divider */}
              <div className="border-t my-4"></div>
              
              {/* Navigation Actions */}
              <Button
                variant="outlined"
                to={`/campaigns/${campaign.id}/leads`}
                className="w-full flex items-center justify-between p-3 h-auto"
              >
                <div className="flex items-center space-x-3">
                  <UsersIcon className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">Manage Leads</span>
                </div>
                <span className="text-sm text-muted-foreground">{campaign.stats?.totalLeads || 0}</span>
              </Button>

              <Button
                variant="outlined"
                to={`/campaigns/${campaign.id}/results`}
                className="w-full flex items-center justify-between p-3 h-auto"
              >
                <div className="flex items-center space-x-3">
                  <ChartBarIcon className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">View Results</span>
                </div>
              </Button>

              {campaign.campaignType === 'text' && (
                <Button
                  variant="outlined"
                  to="/sms/inbox"
                  className="w-full flex items-center justify-between p-3 h-auto"
                >
                  <div className="flex items-center space-x-3">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">SMS Conversations</span>
                  </div>
                  {smsStats && smsStats.openThreads > 0 && (
                    <span className="text-sm text-primary font-semibold">{smsStats.openThreads} open</span>
                  )}
                </Button>
              )}

              {campaign.campaignType !== 'text' && (
                <Button
                  variant="outlined"
                  to={`/campaigns/${campaign.id}/calls`}
                  className="w-full flex items-center justify-between p-3 h-auto"
                >
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Call History</span>
                  </div>
                </Button>
              )}

              {isAdmin && campaign.campaignType !== 'text' && (
                <Button
                  onClick={() => setShowTestCallModal(true)}
                  variant="outlined"
                  className="w-full flex items-center justify-between p-3 h-auto"
                >
                  <div className="flex items-center space-x-3">
                    <BeakerIcon className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Test Call</span>
                  </div>
                </Button>
              )}

              {isAdmin && campaign.campaignType !== 'text' && (
                <Button
                  onClick={async () => {
                    if (!confirm('Sync area codes for all leads in this campaign?')) return;
                    setSyncAreaCodesLoading(true);
                    try {
                      const result = await campaignService.syncAreaCodes(campaign.id);
                      toast.success(`Synced area codes for ${result.totalUpdated} leads`);
                    } catch (err: any) {
                      toast.error(err?.response?.data?.message || 'Sync failed');
                    } finally {
                      setSyncAreaCodesLoading(false);
                    }
                  }}
                  variant="outlined"
                  className="w-full flex items-center justify-between p-3 h-auto"
                  disabled={syncAreaCodesLoading}
                >
                  <div className="flex items-center space-x-3">
                    <UsersIcon className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">
                      {syncAreaCodesLoading ? 'Syncing leads...' : 'Sync Area Codes'}
                    </span>
                  </div>
                </Button>
              )}

              {isAdmin && (
                <Button
                  onClick={() => setShowTestLeadImportModal(true)}
                  variant="outlined"
                  className="w-full flex items-center justify-between p-3 h-auto"
                >
                  <div className="flex items-center space-x-3">
                    <UsersIcon className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Import Test Leads</span>
                  </div>
                </Button>
              )}
            </div>
            </CardContent>
          </Card>

          {/* Campaign Stats */}
          {campaign.campaignType === 'text' ? (
            <Card className="p-6">
              <SectionHeader title="SMS Statistics" className="mb-4" />
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Leads</span>
                    <span className="font-medium">{campaign.stats?.totalLeads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SMS Sent</span>
                    <span className="font-medium">{smsStats?.totalSent || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Inbound Replies</span>
                    <span className="font-medium text-success">{smsStats?.inboundReplies || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Open Threads</span>
                    <span className="font-medium">{smsStats?.openThreads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">DNC / Opt-Out</span>
                    <span className="font-medium text-destructive">{smsStats?.dncBlocks || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivered</span>
                    <span className="font-medium text-success">{smsStats?.delivered ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Failed / Undelivered</span>
                    <span className="font-medium text-destructive">{smsStats?.failed ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pending</span>
                    <span className="font-medium">{smsStats?.pending ?? 0}</span>
                  </div>
                  {(smsStats?.totalSent || 0) > 0 && (
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground">Reply Rate</span>
                      <span className="font-medium">
                        {Math.round(((smsStats?.inboundReplies || 0) / (smsStats?.totalSent || 1)) * 100)}%
                      </span>
                    </div>
                  )}
                  {smsStats?.stepBreakdown && smsStats.stepBreakdown.length > 0 && (
                    <div className="pt-3 border-t space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        By sequence step
                      </div>
                      {Object.entries(
                        smsStats.stepBreakdown.reduce<Record<number, Record<string, number>>>((acc, row) => {
                          const step = Number(row.sequence_step_number);
                          if (!acc[step]) acc[step] = {};
                          acc[step][row.status] = Number(row.count);
                          return acc;
                        }, {})
                      )
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([step, statuses]) => (
                          <div key={step} className="text-xs flex justify-between gap-2">
                            <span className="text-muted-foreground">
                              Step {step}
                              {Number(step) === 1 ? ' (initial)' : ` (follow-up ${Number(step) - 1})`}
                            </span>
                            <span className="font-mono text-foreground">
                              {Object.entries(statuses)
                                .map(([st, n]) => `${st}:${n}`)
                                .join(' · ')}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-6">
              <SectionHeader title="Campaign Statistics" className="mb-4" />
              <CardContent className="p-0">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Leads</span>
                    <span className="font-medium">{campaign.stats?.totalLeads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Remaining Leads</span>
                    <span className="font-medium text-warning">{campaign.stats?.remainingLeads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Calls Made</span>
                    <span className="font-medium">{campaign.stats?.successfulCalls || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Active Calls</span>
                    <span className="font-medium">{campaign.stats?.activeCalls || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">No Answer</span>
                    <span className="font-medium">{campaign.stats?.noAnswerLeads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Failed Calls</span>
                    <span className="font-medium">{campaign.stats?.failedCalls || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Interested</span>
                    <span className="font-medium text-success">{campaign.stats?.interestedLeads || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Not Interested</span>
                    <span className="font-medium text-destructive">{campaign.stats?.notInterestedLeads || 0}</span>
                  </div>
                  {(campaign.stats?.totalCalls || 0) > 0 && (
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-muted-foreground">Interest Rate</span>
                      <span className="font-medium">
                        {campaign.stats?.interestRate ? `${campaign.stats.interestRate}%` : '0%'}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Test Call Modal */}
      <Modal
        isOpen={showTestCallModal}
        onClose={closeTestCallModal}
        title="Test Call"
        size="md"
      >
        <div className="space-y-6">
          <div>
            <label htmlFor="testPhone" className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="testPhone"
              value={testCallPhone}
              onChange={(e) => setTestCallPhone(e.target.value)}
              placeholder="+1234567890"
              className="input"
              disabled={testCallLoading}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Enter phone number in E.164 format (e.g., +16784088584)
            </p>
            {campaign?.campaignType === 'human' && (
              <p className="mt-2 text-xs text-muted-foreground">
                When they answer, <span className="font-medium text-foreground">your browser phone</span> will
                ring — not campaign agents. Keep this tab open
                {webrtc && webrtc.status !== 'ready' ? ' and wait until WebRTC shows ready.' : '.'}
              </p>
            )}
          </div>
          
          {testCallResult && (
            <div className={`p-4 rounded-md ${
              testCallResult.success 
                ? 'bg-success/10 border border-success/20'
                : 'bg-destructive/10 border border-destructive/20'
            }`}>
              <div className="flex items-start space-x-3">
                {testCallResult.success ? (
                  <CheckCircleIcon className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    testCallResult.success 
                      ? 'text-success'
                      : 'text-destructive'
                  }`}>
                    {testCallResult.success ? 'Test Call Initiated' : 'Test Call Failed'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    testCallResult.success 
                      ? 'text-success/80'
                      : 'text-destructive/80'
                  }`}>
                    {testCallResult.message}
                  </p>
                  {testCallResult.callId && (
                    <p className="text-xs text-success mt-1">
                      Call ID: {testCallResult.callId}
                    </p>
                  )}
                  {testCallResult.success && campaign?.campaignType === 'bot' && (
                    <Button
                      variant="outlined"
                      size="sm"
                      className="mt-3"
                      to="/admin/bot-monitor"
                      onClick={() => setShowTestCallModal(false)}
                    >
                      Open Bot Monitor (live transcript)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 pt-2 border-t border-border">
            <Button
              variant="outlined"
              onClick={closeTestCallModal}
              disabled={testCallLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTestCall}
              loading={testCallLoading}
              disabled={!testCallPhone.trim() || testCallLoading}
            >
              <BeakerIcon className="-ml-1 mr-2 h-4 w-4" />
              Start Test Call
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Test Leads Modal */}
      <Modal
        isOpen={showTestLeadImportModal}
        onClose={closeTestLeadImportModal}
        title="Import Test Leads"
        size="md"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of test leads
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={testLeadImportCount === 10 ? 'primary' : 'outlined'}
                onClick={() => setTestLeadImportCount(10)}
                disabled={testLeadImportLoading}
              >
                Import 10
              </Button>
              <Button
                variant={testLeadImportCount === 20 ? 'primary' : 'outlined'}
                onClick={() => setTestLeadImportCount(20)}
                disabled={testLeadImportLoading}
              >
                Import 20
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              System will generate dummy leads with test phone numbers for this campaign.
            </p>
          </div>

          {testLeadImportResult && (
            <div className="p-4 rounded-md bg-success/10 border border-success/20">
              <p className="text-sm font-medium text-success">
                Test leads imported
              </p>
              <p className="text-sm text-success/80 mt-1">
                Requested: {testLeadImportResult.requested}, Imported: {testLeadImportResult.imported}
              </p>
            </div>
          )}

          {testLeadClearResult && (
            <div className="p-4 rounded-md bg-warning/10 border border-warning/20">
              <p className="text-sm font-medium text-warning">
                Test leads cleared
              </p>
              <p className="text-sm text-warning/80 mt-1">
                Found: {testLeadClearResult.found}, Deleted: {testLeadClearResult.deleted}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-2 border-t border-border">
            <Button
              variant="destructive"
              onClick={handleClearTestLeads}
              loading={testLeadClearLoading}
              disabled={testLeadImportLoading || testLeadClearLoading}
            >
              Clear Test Leads
            </Button>
            <Button
              variant="outlined"
              onClick={closeTestLeadImportModal}
              disabled={testLeadImportLoading || testLeadClearLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleImportTestLeads}
              loading={testLeadImportLoading}
              disabled={testLeadImportLoading || testLeadClearLoading}
            >
              Import {testLeadImportCount}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteLoading}
        variant="destructive"
      />
    </div>
  );
};

export default CampaignDetailsPage;
